import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import FileLayout from '../../Layouts/FileLayout';

import { faDownload, faFile, faShare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import ProductTitle from '../../Components/Files/ProductTitle';

function App() {
    const [supply, setSupply] = useState({});
    const [loading, setLoading] = useState(true);
    const [downloadLoading, setDownloadLoading] = useState(false);

    const navigate = useNavigate();
    const { slug, id } = useParams();
    const baseUrl = process.env.REACT_APP_API_URL;

    useEffect(() => {
        const fetchFile = async () => {
            let redirected = false;

            try {
                const response = await fetch(`${baseUrl}/arquivos/${slug}/arquivos/${id}`, {
                    method: 'GET',
                });

                if (!response.ok) {
                    if (response.status === 404) {
                        navigate('/erro/404', {
                            state: { message: { type: '404', text: 'Arquivo não encontrado!' } },
                        });
                        redirected = true;
                        return;
                    } else if (response.status === 403) {
                        navigate('/erro/403', {
                            state: { message: { type: '403', text: 'Você não tem permissão para acessar essa página.' } },
                        });
                        redirected = true;
                        return;
                    }

                    throw new Error('Erro ao buscar a material.');
                }

                const data = await response.json();
                setSupply(data.material);
            } catch (error) {
                console.error('Erro na requisição de conteúdo', error);
            } finally {
                if (!redirected) {
                    setLoading(false);
                }
            }
        };

        fetchFile();
    }, []);

    useEffect(() => {
        if (!supply) return;
        const updateViews = async () => {
            try {
                const response = await fetch(`${baseUrl}/arquivos/${supply.produto_slug}/visualizar/material/${supply.id}`, {
                    method: 'PUT',
                });

                if (!response.ok) {
                    throw new Error('Erro ao atualizar progresso.');
                }
            } catch (error) {
                console.error('Erro na requisição de conteúdo', error);
            }
        };

        updateViews();
    }, [supply]);

    const handleDownload = async (e) => {
        e.preventDefault();
        setDownloadLoading(true);

        try {
            const response = await fetch(`${baseUrl}/arquivos/${supply.produto_slug}/arquivos/${supply.id}/download`, {
                method: 'GET',
            });

            if (!response.ok) {
                throw new Error(`Erro ao baixar arquivo: ${response.statusText}`);
            }

            const blob = await response.blob();

            let fileName = supply.nome;
            const contentDisposition = response.headers.get('Content-Disposition');

            if (contentDisposition) {
                const match = contentDisposition.match(/filename="?([^"]+)"?/);
                if (match && match[1]) {
                    fileName = match[1];
                }
            }

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();

            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Erro na requisição:', error);
        } finally {
            setDownloadLoading(false);
        }
    };

    const handleShare = async () => {
        const shareUrl = `${window.location.origin}/arquivos/${supply.produto_slug}/arquivos/${supply.id}`;
        const shareText = `Veja essa material da Rometal: ${supply.nome}`;

        if (navigator.share) {
            try {
                await navigator.share({
                    title: 'Compartilhar',
                    text: shareText,
                    url: shareUrl,
                });
            } catch (error) {
                console.error('Erro ao compartilhar:', error);
            }
        } else {
            alert('O compartilhamento não é suportado neste navegador.');
        }
    };

    if (loading)
        return (
            <div className="mx-auto py-20 flex justify-center">
                <div className="h-16 w-16 animate-spin rounded-full border-4 border-solid border-primary border-t-transparent"></div>
            </div>
        );

    return (
        <FileLayout>
            <div className="pb-4 md:pb-6 2xl:pb-10">
                <div className="grid grid-cols-12 gap-4 md:gap-6">
                    <div className="col-span-12">
                        <ProductTitle
                            productName={supply.nome}
                            loading={loading}
                            breadcrumb={[
                                { label: supply.produto_nome, link: `/painel/produtos/${slug}` },
                                { label: 'Materiais', link: `/painel/produtos/${slug}/files` },
                            ]}
                        />

                        <div className="flex flex-col items-center -mb-10">
                            {supply.preview ? (
                                <img src={supply.preview} className="rounded-xl border border-neutral-500 mb-4" loading="lazy" />
                            ) : (
                                <div className="relative w-full max-w-[320px] aspect-[32/43] bg-white rounded-xl border border-neutral-500 mb-4">
                                    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                                        <span className="fa-layers fa-fw text-9xl">
                                            <FontAwesomeIcon icon={faFile} className="text-secondary" />
                                            <span className="fa-layers-text fa-inverse text-2xl font-black mt-2 uppercase">
                                                {supply.extensao}
                                            </span>
                                        </span>
                                    </div>
                                </div>
                            )}

                            <div className="flex gap-4 mt-3 mb-20">
                                {supply.baixavel && (
                                    <button
                                        className="group flex items-center border-2 border-secondary rounded-md text-white font-semibold uppercase max-sm:text-sm sm:tracking-wide px-4 py-2 transition-all hover:bg-secondary disabled:opacity-60 disabled:cursor-not-allowed"
                                        onClick={handleDownload}
                                        disabled={downloadLoading}
                                    >
                                        {downloadLoading ? (
                                            <svg
                                                className="animate-spin mr-2 h-4 w-4 text-secondary group-hover:text-white"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                            >
                                                <circle
                                                    className="opacity-25"
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                    stroke="currentColor"
                                                    strokeWidth="4"
                                                />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                            </svg>
                                        ) : (
                                            <FontAwesomeIcon
                                                icon={faDownload}
                                                className="mr-2 text-secondary transition-all group-hover:text-white"
                                            />
                                        )}
                                        {downloadLoading ? 'Baixando...' : 'Download'}
                                    </button>
                                )}
                                {supply.compartilhavel && (
                                    <button
                                        className="group flex items-center border-2 border-secondary rounded-md text-white font-semibold uppercase tracking-wide px-4 py-2 transition-all hover:bg-secondary"
                                        onClick={handleShare}
                                    >
                                        <FontAwesomeIcon
                                            icon={faShare}
                                            className="mr-2 text-secondary transition-all group-hover:text-white"
                                        />
                                        Compartilhar
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </FileLayout>
    );
}

export default App;
